// =============================================================================
// Origin UI — src/pages/evaluations/TeamEvaluationsPage.tsx
// Team evaluations list — managers see direct reports, HR sees all.
// Now with column filters for quick searching.
// Phase 7 — Year-End Evaluation & Probation Review module.
// =============================================================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { evaluationApi, EvaluationListItem } from '../../services/evaluationApi';
import styles from './evaluations.module.css';

export function TeamEvaluationsPage() {

  const navigate = useNavigate();
  const [evaluations, setEvaluations] = useState<EvaluationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { roles } = useAuth();
  const [companyEvaluations, setCompanyEvaluations] = useState<any[]>([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyFilters, setCompanyFilters] = useState({ evaluation: '', employee: '', department: '', office: '', manager: '', status: '', rating: '' });
  const [sortCol, setSortCol] = useState<string>('employeeName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };
  const sortIcon = (col: string) => sortCol === col ? (sortDir === 'asc' ? ' \u2191' : ' \u2193') : '';

  // Column filters
  const [cycleFilter, setCycleFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [officeFilter, setOfficeFilter] = useState('');
  const [managerFilter, setManagerFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ratingFilter, setRatingFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await evaluationApi.getTeamEvaluations();
        setEvaluations(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }

    })();
  }, []);

  // Load company evaluations when roles are available
  useEffect(() => {
    (async () => {
      setCompanyLoading(true);
      try {
        const compData = await evaluationApi.getCompanyEvaluations();
        setCompanyEvaluations(compData);
      } catch {} finally { setCompanyLoading(false); }
    })();
  }, [roles.isHRAdmin]);

  // Build unique values for dropdowns
  const cycleNames = useMemo(() => [...new Set(evaluations.map(e => e.cycleName))].sort(), [evaluations]);
  const departments = useMemo(() => [...new Set(evaluations.map(e => e.department))].sort(), [evaluations]);
  const offices = useMemo(() => [...new Set(evaluations.map(e => e.office).filter(Boolean))].sort(), [evaluations]);
  const managers = useMemo(() => [...new Set(evaluations.map(e => e.managerName).filter(Boolean))].sort(), [evaluations]);
  const statuses = useMemo(() => [...new Set(evaluations.map(e => e.status))].sort(), [evaluations]);
  const ratings = useMemo(() => [...new Set(evaluations.map(e => e.overallRating).filter(Boolean))].sort() as string[], [evaluations]);

  // Filtered list
  const filtered = useMemo(() => {
    return evaluations.filter(e => {
      if (cycleFilter && e.cycleName !== cycleFilter) return false;
      if (nameFilter && !(e.employeeName.toLowerCase().includes(nameFilter.toLowerCase()) || e.employeeNumber.toLowerCase().includes(nameFilter.toLowerCase()))) return false;
      if (deptFilter && e.department !== deptFilter) return false;
      if (officeFilter && e.office !== officeFilter) return false;
      if (managerFilter && e.managerName !== managerFilter) return false;
      if (statusFilter && e.status !== statusFilter) return false;
      if (ratingFilter && (e.overallRating || '') !== ratingFilter) return false;
      return true;
    });
  }, [evaluations, nameFilter, deptFilter, officeFilter, managerFilter, statusFilter, ratingFilter]);

  const hasFilters = cycleFilter || nameFilter || deptFilter || officeFilter || managerFilter || statusFilter || ratingFilter;

  const clearFilters = () => {
    setCycleFilter(''); setNameFilter(''); setDeptFilter(''); setOfficeFilter('');
    setManagerFilter(''); setStatusFilter(''); setRatingFilter('');
  };

  if (loading) return <div className={styles.page}><div className={styles.loading}>Loading team evaluations...</div></div>;
  if (error) return <div className={styles.page}><div className={styles.errorBanner}>{error}</div></div>;

  const completed = evaluations.filter(e => e.status === 'ManagerCompleted' || e.status === 'Acknowledged').length;
  const total = evaluations.length;

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Team Evaluations</h1>

      {total > 0 && (
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }} />
          <span className={styles.progressLabel}>{completed} of {total} completed</span>
        </div>
      )}

      {evaluations.length === 0 ? (
        <div className={styles.emptyState}>No evaluations found for the current cycle.</div>
      ) : (
        <>
          {hasFilters && (
            <div className={styles.filterSummary}>
              Showing {filtered.length} of {evaluations.length} evaluations
              <button className={styles.clearFiltersBtn} onClick={clearFilters}>Clear all filters</button>
            </div>
          )}
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => toggleSort('cycleName')} style={{ cursor: 'pointer' }}>Evaluation{sortIcon('cycleName')}</th>
                <th onClick={() => toggleSort('employeeName')} style={{ cursor: 'pointer' }}>Employee{sortIcon('employeeName')}</th>
                <th onClick={() => toggleSort('department')} style={{ cursor: 'pointer' }}>Department{sortIcon('department')}</th>
                <th onClick={() => toggleSort('office')} style={{ cursor: 'pointer' }}>Office{sortIcon('office')}</th>
                <th onClick={() => toggleSort('managerName')} style={{ cursor: 'pointer' }}>Manager{sortIcon('managerName')}</th>
                <th onClick={() => toggleSort('status')} style={{ cursor: 'pointer' }}>Status{sortIcon('status')}</th>
                <th onClick={() => toggleSort('overallRating')} style={{ cursor: 'pointer' }}>Rating{sortIcon('overallRating')}</th>
                <th onClick={() => toggleSort('level1')} style={{ cursor: 'pointer' }}>Submission (Level 1){sortIcon('level1')}</th>
                <th onClick={() => toggleSort('level2')} style={{ cursor: 'pointer' }}>Submission (Level 2){sortIcon('level2')}</th>
              </tr>
              <tr className={styles.filterHeaderRow}>
                <th>
                  <select className={styles.columnFilter} value={cycleFilter} onChange={e => setCycleFilter(e.target.value)}>
                    <option value="">All</option>
                    {cycleNames.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </th>
                <th>
                  <input
                    className={styles.columnFilter}
                    placeholder="Search name or ID…"
                    value={nameFilter}
                    onChange={e => setNameFilter(e.target.value)}
                  />
                </th>
                <th>
                  <select className={styles.columnFilter} value={deptFilter} onChange={e => setDeptFilter(e.target.value)}>
                    <option value="">All</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </th>
                <th>
                  <select className={styles.columnFilter} value={officeFilter} onChange={e => setOfficeFilter(e.target.value)}>
                    <option value="">All</option>
                    {offices.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </th>
                <th>
                  <select className={styles.columnFilter} value={managerFilter} onChange={e => setManagerFilter(e.target.value)}>
                    <option value="">All</option>
                    {managers.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </th>
                <th>
                  <select className={styles.columnFilter} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="">All</option>
                    {statuses.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
                  </select>
                </th>
                <th>
                  <select className={styles.columnFilter} value={ratingFilter} onChange={e => setRatingFilter(e.target.value)}>
                    <option value="">All</option>
                    {ratings.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...filtered].sort((a: any, b: any) => {
              const av = sortCol.startsWith('level') ? '' : (a[sortCol] || '');
              const bv = sortCol.startsWith('level') ? '' : (b[sortCol] || '');
              if (av < bv) return sortDir === 'asc' ? -1 : 1;
              if (av > bv) return sortDir === 'asc' ? 1 : -1;
              return 0;
            }).map(e => (
                <tr key={e.evaluationId} onClick={() => navigate(`/evaluations/${e.evaluationId}`)} className={styles.clickableRow}>
                  <td>{e.cycleName}</td>
                  <td>
                    <div className={styles.employeeName}>{e.employeeName}</div>
                    <div className={styles.employeeNumber}>{e.employeeNumber}</div>
                  </td>
                  <td>{e.department}</td>
                  <td>{e.office}</td>
                  <td>{e.managerName}</td>
                  <td><span style={{ 
                    color: (e.status === 'Acknowledged') ? '#059669' 
                      : (e.status === 'ManagerCompleted' || e.status === 'EmployeeSubmitted') ? '#D97706' 
                      : '#6B7280',
                    fontWeight: 600 
                  }}>{formatStatus(e.status)}</span></td>
                  <td>{e.overallRating || '—'}</td>
                  <td>{e.cycleType === 'MidYearReview'
                    ? (e.managerCompletedAt ? new Date(e.managerCompletedAt).toLocaleDateString('en-GB') : '—')
                    : (e.employeeSubmittedAt ? new Date(e.employeeSubmittedAt).toLocaleDateString('en-GB') : '—')
                  }</td>
                  <td>{e.cycleType === 'MidYearReview'
                    ? (e.employeeAcknowledgedAt ? new Date(e.employeeAcknowledgedAt).toLocaleDateString('en-GB') : '—')
                    : (e.managerCompletedAt ? new Date(e.managerCompletedAt).toLocaleDateString('en-GB') : '—')
                  }</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: '#888' }}>No evaluations match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {/* Company Evaluations — HR Admin only */}
      {roles.isHRAdmin && (
        <div style={{ marginTop: 32 }}>
          <h2 className={styles.pageTitle} style={{ fontSize: '1.3rem', marginBottom: 12 }}>Company Evaluations</h2>
          <p style={{ fontSize: 13, color: '#6B7885', marginBottom: 16 }}>All evaluations across the company. Visible to HR Admins only.</p>
          {companyLoading ? <div className={styles.loading}>Loading company evaluations...</div> : companyEvaluations.length === 0 ? <p style={{ color: '#6B7885', fontSize: 14 }}>No evaluations found.</p> : (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#6B7885' }}>
                {companyEvaluations.filter((e: any) => e.status === 'ManagerCompleted' || e.status === 'Acknowledged').length} of {companyEvaluations.length} completed
              </div>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Evaluation</th>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Office</th>
                    <th>Manager</th>
                    <th>Status</th>
                    <th>Rating</th>
                    <th>Submission (Level 1)</th>
                    <th>Submission (Level 2)</th>
                  </tr>
                  <tr>
                    <td><select className={styles.filterSelect} value={companyFilters.evaluation} onChange={e => setCompanyFilters({...companyFilters, evaluation: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.map((e: any) => e.cycleName))].map(n => <option key={n} value={n}>{n}</option>)}</select></td>
                    <td><input className={styles.filterInput} placeholder="Search name or ID..." value={companyFilters.employee} onChange={e => setCompanyFilters({...companyFilters, employee: e.target.value})} /></td>
                    <td><select className={styles.filterSelect} value={companyFilters.department} onChange={e => setCompanyFilters({...companyFilters, department: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.map((e: any) => e.department))].sort().map(d => <option key={d} value={d}>{d}</option>)}</select></td>
                    <td><select className={styles.filterSelect} value={companyFilters.office} onChange={e => setCompanyFilters({...companyFilters, office: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.map((e: any) => e.office))].sort().map(o => <option key={o} value={o}>{o}</option>)}</select></td>
                    <td><select className={styles.filterSelect} value={companyFilters.manager} onChange={e => setCompanyFilters({...companyFilters, manager: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.map((e: any) => e.managerName))].sort().map(m => <option key={m} value={m}>{m}</option>)}</select></td>
                    <td><select className={styles.filterSelect} value={companyFilters.status} onChange={e => setCompanyFilters({...companyFilters, status: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.map((e: any) => e.status))].map(s => <option key={s} value={s}>{s}</option>)}</select></td>
                    <td><select className={styles.filterSelect} value={companyFilters.rating} onChange={e => setCompanyFilters({...companyFilters, rating: e.target.value})}><option value="">All</option>{[...new Set(companyEvaluations.filter((e: any) => e.overallRating).map((e: any) => e.overallRating))].map(r => <option key={r} value={r}>{r}</option>)}</select></td>
                    <td></td>
                    <td></td>
                  </tr>
                </thead>
                <tbody>
                  {companyEvaluations
                    .filter((e: any) => {
                      if (companyFilters.evaluation && e.cycleName !== companyFilters.evaluation) return false;
                      if (companyFilters.employee && !e.employeeName.toLowerCase().includes(companyFilters.employee.toLowerCase()) && !e.employeeNumber?.toLowerCase().includes(companyFilters.employee.toLowerCase())) return false;
                      if (companyFilters.department && e.department !== companyFilters.department) return false;
                      if (companyFilters.office && e.office !== companyFilters.office) return false;
                      if (companyFilters.manager && e.managerName !== companyFilters.manager) return false;
                      if (companyFilters.status && e.status !== companyFilters.status) return false;
                      if (companyFilters.rating && e.overallRating !== companyFilters.rating) return false;
                      return true;
                    })
                    .map((e: any) => (
                      <tr key={e.evaluationId} onClick={() => navigate(`/evaluations/${e.evaluationId}`)} style={{ cursor: 'pointer' }}>
                        <td>{e.cycleName}</td>
                        <td className={styles.bold}>{e.employeeName}<br/><span className={styles.muted}>{e.employeeNumber}</span></td>
                        <td>{e.department}</td>
                        <td>{e.office}</td>
                        <td>{e.managerName}</td>
                        <td><span style={{ color: e.status === 'Acknowledged' ? '#059669' : (e.status === 'ManagerCompleted' || e.status === 'EmployeeSubmitted') ? '#D97706' : '#6B7280', fontWeight: 600 }}>{formatStatus(e.status)}</span></td>
                        <td>{e.overallRating || '—'}</td>
                        <td>{e.cycleType === 'MidYearReview'
                          ? (e.managerCompletedAt ? new Date(e.managerCompletedAt).toLocaleDateString('en-GB') : '—')
                          : (e.employeeSubmittedAt ? new Date(e.employeeSubmittedAt).toLocaleDateString('en-GB') : '—')
                        }</td>
                        <td>{e.cycleType === 'MidYearReview'
                          ? (e.employeeAcknowledgedAt ? new Date(e.employeeAcknowledgedAt).toLocaleDateString('en-GB') : '—')
                          : (e.managerCompletedAt ? new Date(e.managerCompletedAt).toLocaleDateString('en-GB') : '—')
                        }</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function formatStatus(status: string): string {
  switch (status) {
    case 'NotStarted': return 'Not Started';
    case 'EmployeeDraft': return 'Employee Draft';
    case 'EmployeeSubmitted': return 'Level 1 Complete';
    case 'ManagerDraft': return 'Manager Draft';
    case 'ManagerCompleted': return 'Level 1 Complete';
    case 'Acknowledged': return 'Acknowledged';
    default: return status;
  }
}
